import { useTranslation } from 'next-i18next/pages';
import type { RefObject } from 'react';

import { useMediaQuery } from '@/hooks/shared/useMediaQuery';

import theme from '@/styles/theme';

import Area from '../../layouts/shared/Area';
import { IconArrowLeftRound, IconArrowRightRound } from '../../ui/icon';
import SectionTitle from '../../ui/title/SectionTitle';

/*
 * 예전에는 어드민의 ButtonIcon 을 그대로 끌어다 썼다(공개 페이지가 어드민 컴포넌트를,
 * 나아가 emotion 런타임을 끌고 오던 유일한 경로).
 * 외형을 바꾸지 않기 위해 그 컴포넌트의 heavy(기본) 상태 스타일을 그대로 옮긴다.
 */
const CAROUSEL_BUTTON =
  'inline-flex h-9 w-9 cursor-pointer appearance-none items-center justify-center border-none outline-none disabled:pointer-events-none disabled:opacity-40';

type Props = {
  nextElRef: RefObject<HTMLDivElement | null>;
  prevElRef: RefObject<HTMLDivElement | null>;
};

/*
 * 원본의 `> div` 규칙을 각 div 에 직접 붙인다.
 * div 는 항상 두 개(이전/다음)라 first-of-type = 첫 번째, last-of-type = 두 번째다.
 * ::after 는 원본에 content 가 없어 실제로 생성되지 않던 선언인데,
 * Tailwind 의 after: 변형은 content 를 함께 넣으므로 [&::after] 로 옮긴다.
 */
const SWIPER_BUTTON_BASE = 'relative w-full cursor-pointer [&::after]:hidden';

export default function AboutNewsRoomHeader({ nextElRef, prevElRef }: Props) {
  const { t } = useTranslation('common');
  const isDesktop = useMediaQuery({
    query: `(min-width: ${theme`screens.lg`})`,
  });

  return (
    <Area>
      <div className="relative mb-[clamp(40px,4.167vw,80px)]">
        <SectionTitle
          title={t('뉴스룸')}
          linkInfo={{
            context: t('뉴스룸'),
            title: t('전체 보기'),
            url: '/about/news',
          }}
          iconRight={null}
          buttonWrapStyle={{
            padding: isDesktop ? '12px 0' : '8px 0',
          }}
        />
        <div className="absolute right-0 top-0 z-[2] hidden h-full gap-[8px] lg:flex">
          <div
            ref={prevElRef}
            className={`${SWIPER_BUTTON_BASE} left-auto right-0`}
          >
            <button
              type="button"
              className={CAROUSEL_BUTTON}
              aria-label={t('이전 뉴스')}
            >
              <IconArrowLeftRound
                size={32}
                color={theme`colors.apps.brandGrayBlack`}
                aria-hidden="true"
              />
            </button>
          </div>
          <div ref={nextElRef} className={`${SWIPER_BUTTON_BASE} right-0`}>
            <button
              type="button"
              className={CAROUSEL_BUTTON}
              aria-label={t('다음 뉴스')}
            >
              <IconArrowRightRound
                size={32}
                color={theme`colors.apps.brandGrayBlack`}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </div>
    </Area>
  );
}
